import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { UserManager, type User as OidcUser, WebStorageStateStore } from 'oidc-client-ts';
// `@jeket-cloud/types` compiles to CommonJS (Node16). Rollup's named-export
// static analysis can't see `exports.X = X` named exports declared before
// the function body, so we import the namespace and destructure at runtime
// for runtime values, and use `import type` for type-only references.
// (This is the same workaround Vite's docs recommend for CJS workspace deps.)
import { filterKnownRoles } from '@jeket-cloud/types/auth';
import type { Role } from '@jeket-cloud/types/auth';
import { getOrgOverride } from '@/api/client';
import { UPDATE_PASSWORD } from '@/lib/kcAction';
import { fetchAuthMe, type AuthMe } from '@/lib/authMe';
interface User {
  userId: string;
  username: string;
  /**
   * Legacy single-role "tier" from the token's realm roles. Only `superuser`
   * still gates anything (Header, Deployments). New code should consume
   * {@link User.roles} via the {@link useUserRoles} hook and gate UI through
   * `<RoleGate>`; to show a user their role, use `displayRole` (#860).
   */
  role: 'superuser' | 'admin' | 'operator' | 'viewer';
  /**
   * Operator-persona roles the BFF enforces in the acting org (its
   * /api/auth/me: realm grants plus the org-scoped role, #847); the OIDC
   * token's `realm_access.roles` until it answers. Filtered to known
   * {@link Role} enum values. Source of truth for `<RoleGate>` and the
   * role-scoped operations UI (epic #188).
   */
  roles: Role[];
  /**
   * The user's role in the acting org, from the BFF's /api/auth/me (#847);
   * null when they have none, undefined until it answers.
   */
  orgRole?: Role | null;
  /**
   * True when the BFF's /api/auth/me says the account belongs to no
   * organization and is neither a superuser nor Jeket staff (JSS#849
   * follow-up): every org-scoped call answers 403 no_organization, so the app
   * shows the NoOrganization screen instead. Undefined until it answers.
   */
  noOrganization?: boolean;
  /**
   * The token carries realm role `jeket-staff`: the role corp-console's gate
   * requires (#1156). Read from the same verified-at-sign-in realm roles as
   * `role`, so it is known immediately; it decides whether to show links into
   * the staff console (which re-verifies the role server-side) and the
   * org-required gate's staff wording (#1149).
   */
  staff: boolean;
  /**
   * The org the BFF acts in for this session, from /api/auth/me (#1149):
   * the X-User-Org it forwards to services. null when it forwards none (a
   * superuser with no pick, staff in no org, a member of several orgs who
   * has not picked one); undefined until it answers.
   */
  actingOrgId?: string | null;
  /**
   * The BFF will not act in the saved org-switcher pick (#1171): a
   * non-superuser's pick outside their orgs, or a superuser's of an org that
   * no longer exists. From /api/auth/me; undefined until it answers.
   */
  orgOverrideRejected?: boolean;
  email?: string;
  name?: string;
  orgId?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
}

/**
 * Whether the BFF's /api/auth/me has answered for the signed-in user:
 * `pending` until it does (and while no one is signed in), `ready` once its
 * roles and `orgRole` are in {@link User}, `error` if it failed or answered
 * nothing usable, in which case `user.roles` stay the token's.
 */
export type MeStatus = 'pending' | 'ready' | 'error';

interface AuthContextType extends AuthState {
  meStatus: MeStatus;
  /**
   * Sign in through Keycloak. `returnTo` is the page to come back to
   * (vetted by /auth/callback's callbackDestination); it defaults to the
   * current location.
   */
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  register: () => Promise<void>;
  /** Send the user to Keycloak's own password change and back here (#998). */
  changePassword: () => Promise<void>;
  getAccessToken: () => string | null;
  getUserManager: () => UserManager | null;
  isSuperUser: () => boolean;
  isAdmin: () => boolean;
  isOperator: () => boolean;
  isViewer: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_PRIORITY = ['superuser', 'admin', 'operator', 'viewer'] as const;

/** corp-console's required realm role (backend auth/token-verifier.ts STAFF_REALM_ROLE). */
export const STAFF_REALM_ROLE = 'jeket-staff';

export function extractUser(oidcUser: OidcUser): User {
  const profile = oidcUser.profile;
  const realmRoles: string[] =
    (profile as any).realm_access?.roles ?? [];

  let role: 'superuser' | 'admin' | 'operator' | 'viewer' = 'viewer';
  for (const r of ROLE_PRIORITY) {
    if (realmRoles.includes(r)) {
      role = r;
      break;
    }
  }

  return {
    userId: profile.sub,
    username: profile.preferred_username ?? profile.sub,
    role,
    roles: filterKnownRoles(realmRoles),
    staff: realmRoles.includes(STAFF_REALM_ROLE),
    email: profile.email,
    name: profile.name,
    orgId: (profile as any).org_id,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });
  const userManagerRef = useRef<UserManager | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    initAuth();
  }, []);

  async function initAuth() {
    try {
      // Fetch OIDC config from backend
      const configRes = await fetch('/api/auth/config');
      const config = await configRes.json();

      const um = new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: `${window.location.origin}/auth/callback`,
        post_logout_redirect_uri: `${window.location.origin}/login`,
        scope: config.scope,
        response_type: 'code',
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      });

      userManagerRef.current = um;

      // Handle silent renew errors
      um.events.addSilentRenewError(() => {
        setState({ isAuthenticated: false, user: null, isLoading: false });
      });

      um.events.addUserLoaded((oidcUser) => {
        setState({
          isAuthenticated: true,
          user: extractUser(oidcUser),
          isLoading: false,
        });
      });

      um.events.addUserUnloaded(() => {
        setState({ isAuthenticated: false, user: null, isLoading: false });
      });

      // Check for existing session
      const oidcUser = await um.getUser();
      if (oidcUser && !oidcUser.expired) {
        setState({
          isAuthenticated: true,
          user: extractUser(oidcUser),
          isLoading: false,
        });
      } else {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } catch (err) {
      console.error('[auth] Failed to initialize OIDC:', err);
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }

  const login = useCallback(async (returnTo?: string) => {
    const um = userManagerRef.current;
    if (!um) return;
    const { pathname, search, hash } = window.location;
    await um.signinRedirect({ state: { returnTo: returnTo ?? pathname + search + hash } });
  }, []);

  const register = useCallback(async () => {
    const um = userManagerRef.current;
    if (!um) return;
    // Keycloak supports the 'create' prompt hint to redirect to its registration page
    await um.signinRedirect({
      prompt: 'create',
      state: { returnTo: '/' },
    });
  }, []);

  // #998: the password is Keycloak's to change, never the BFF's. Ask for its
  // UPDATE_PASSWORD required action on an ordinary authorization request
  // (kc_action): the same PKCE redirect as login, so the answer comes back
  // through /auth/callback, which exchanges the code, reads Keycloak's
  // kc_action_status and returns to this page with it.
  const changePassword = useCallback(async () => {
    const um = userManagerRef.current;
    if (!um) return;
    await um.signinRedirect({
      extraQueryParams: { kc_action: UPDATE_PASSWORD },
      state: { returnTo: window.location.pathname },
    });
  }, []);

  const logout = useCallback(async () => {
    const um = userManagerRef.current;
    if (!um) return;

    try {
      // Also notify backend to revoke tokens
      const oidcUser = await um.getUser();
      if (oidcUser) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: oidcUser.refresh_token }),
        }).catch(() => {});
      }
      await um.signoutRedirect();
    } catch {
      await um.removeUser();
      setState({ isAuthenticated: false, user: null, isLoading: false });
    }
  }, []);

  const getAccessToken = useCallback((): string | null => {
    // Synchronous access to the cached user from oidc-client-ts store
    const raw = sessionStorage.getItem(
      `oidc.user:${userManagerRef.current?.settings.authority}:${userManagerRef.current?.settings.client_id}`
    );
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data.access_token ?? null;
    } catch {
      return null;
    }
  }, []);

  // #847: a customer's persona roles are granted per org by the BFF, not as
  // realm roles, so the token under-reports them (an org admin's token
  // carries no `admin`). Ask the BFF for the roles it enforces in the org
  // this session acts in; until it answers, or if it can't, use the token's.
  const [bffRoles, setBffRoles] = useState<(AuthMe & { userId: string }) | null>(null);
  // The user whose /api/auth/me failed (or answered nothing usable).
  const [meFailedFor, setMeFailedFor] = useState<string | null>(null);
  const userId = state.user?.userId;
  useEffect(() => {
    if (!userId) return;
    const token = getAccessToken();
    if (!token) {
      setMeFailedFor(userId);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    // Times out on its own (fetchAuthMe), so `pending` always ends.
    fetchAuthMe(token, getOrgOverride(), { signal: controller.signal })
      .then((me) => {
        if (!cancelled) setBffRoles({ userId, ...me });
      })
      .catch(() => {
        if (!cancelled) setMeFailedFor(userId);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, getAccessToken]);
  const meStatus: MeStatus =
    userId && bffRoles?.userId === userId ? 'ready' : userId && meFailedFor === userId ? 'error' : 'pending';
  const user = useMemo(
    () =>
      state.user && bffRoles?.userId === state.user.userId
        ? {
            ...state.user,
            roles: bffRoles.roles,
            orgRole: bffRoles.orgRole,
            noOrganization: bffRoles.noOrganization,
            actingOrgId: bffRoles.actingOrgId,
            orgOverrideRejected: bffRoles.orgOverrideRejected,
          }
        : state.user,
    [state.user, bffRoles],
  );

  const isSuperUser = useCallback(() => state.user?.role === 'superuser', [state.user]);
  const isAdmin = useCallback(
    () => state.user?.role === 'superuser' || state.user?.role === 'admin',
    [state.user]
  );
  const isOperator = useCallback(
    () =>
      state.user?.role === 'superuser' ||
      state.user?.role === 'admin' ||
      state.user?.role === 'operator',
    [state.user]
  );
  const isViewer = useCallback(
    () =>
      state.user?.role === 'superuser' ||
      state.user?.role === 'admin' ||
      state.user?.role === 'operator' ||
      state.user?.role === 'viewer',
    [state.user]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        user,
        meStatus,
        login,
        logout,
        register,
        changePassword,
        getAccessToken,
        getUserManager: () => userManagerRef.current,
        isSuperUser,
        isAdmin,
        isOperator,
        isViewer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Returns the authed user's operator-persona roles (`Role[]`) in the acting
 * org (see {@link User.roles}), or `[]` if no user is authed. This is the canonical
 * source for `<RoleGate>` and any code that gates on the role hierarchy
 * (`Admin > Engineer > SiteOperator > EventOperator`).
 *
 * Prefer `<RoleGate>` over reading roles directly — keeps gate logic
 * centralised and discoverable.
 */
export function useUserRoles(): Role[] {
  const { user } = useAuth();
  return user?.roles ?? [];
}

